<template>
  <div class="flex flex-row flex-wrap w-10">
    <HeadTitle
      title="Detalle de configuración de carta"
      description="Formulario de creacion y configuración de una carta en donde se definen los parametros de generación y el alcance de la misma"
    />
    <EditRecordButton @update:is-edit-mode="toggleEditMode" class="mt-2" />
  </div>
  <p></p>
  <br />
  <Step :stepNumber="1" title="Configuración basica">
    <div class="formgrid grid w-12 md:w-6">
      <div class="field col-10 md:col-6 flex flex-column">
        <label for="name">Nombre <span class="text-red-600">*</span></label>
        <InputText
          v-model="name"
          id="name"
          type="text"
          :disabled="!isEditMode"
        />
        <small id="text-error" class="p-error">{{
          nameError || '&nbsp;'
        }}</small>
      </div>
      <div class="field col-10 md:col-6 flex flex-column">
        <label for="description"
          >Descripción <span class="text-red-600">*</span></label
        >
        <InputText
          id="description"
          v-model="description"
          :disabled="!isEditMode"
        />
        <small id="text-error" class="p-error">{{
          descriptionError || '&nbsp;'
        }}</small>
      </div>
      <div class="field col-10 md:col-6 flex flex-column">
        <label for="generationType"
          >Método de generación <span class="text-red-600">*</span></label
        >
        <Dropdown
          id="generationType"
          v-model="generationType"
          :options="approvalMethodsOpts"
          optionLabel="label"
          optionValue="value"
          :disabled="!isEditMode"
        ></Dropdown>
        <small id="text-error" class="p-error">{{
          generationTypedError || '&nbsp;'
        }}</small>
      </div>
      <div
        class="field col-10 md:col-6 flex flex-column"
        v-if="generationType === 'WITH_APPROVAL'"
      >
        <label for="approver"
          >Seleccionar Aprobador <span class="text-red-600">*</span></label
        >
        <Dropdown
          v-model="approver"
          :options="approvers"
          optionLabel="name"
          optionValue="id"
          filter
          :disabled="!isEditMode"
        ></Dropdown>
        <small id="text-error" class="p-error">{{
          approverError || '&nbsp;'
        }}</small>
      </div>
      <div class="field col-10 md:col-6 flex flex-column">
        <label for="payrolls"
          >Seleccionar nóminas <span class="text-red-600">*</span></label
        >
        <MultiSelectDialog
          header-title="Seleccionar nóminas"
          label="Seleccionar nóminas"
          :options="payrolls || []"
          v-model="selectedPayrolls"
          show-key="name"
          value-key="id"
          :required="true"
          :disabled="!isEditMode"
        />
        <small id="text-error" class="p-error">{{
          payrollsError || '&nbsp;'
        }}</small>
      </div>
      <div class="field col-10 md:col-8 flex flex-column">
        <div class="flex flex-row mb-2">
          <label for="emails">Correos a notificar</label>
          <InfoButton
            message="Se enviara una copia de la carta generada a los correos definidos en este campo"
          />
        </div>
        <Chips
          v-model="emailsToNotify"
          separator=","
          class="w-12"
          invalid
          :disabled="!isEditMode"
        />
        <small id="text-error" class="p-error">{{
          emailsError || '&nbsp;'
        }}</small>
      </div>
    </div>
  </Step>
  <Step
    :stepNumber="2"
    title="Configurar aprobadores"
    v-if="generationType === 'WITH_MULTIPLE_APPROVALS'"
  >
    <LetterApproversForm
      v-model="approversData"
      :approvers-options="approvers || []"
      :max-items="MAX_APPROVERS"
      :disabled="!isEditMode || isSubmitting"
      :error-message="approversError"
    />
  </Step>
  <Step
    :stepNumber="generationType === 'WITH_MULTIPLE_APPROVALS' ? 3 : 2"
    title="Preparar documentos"
  >
    <Button
      label="Abrir plantilla"
      class="text-sm font-light"
      icon="pi pi-window-maximize"
      @click="isEditorModalVisible = !isEditorModalVisible"
      :disabled="!isEditMode || isSubmitting"
    ></Button>
  </Step>
  <Footer>
    <template #left>
      <AuditButton
        :entities="['LetterCertifConfig']"
        :record-ids="[letterConfigId]"
      />
    </template>
    <ConfirmPopup></ConfirmPopup>
    <Button
      label="Cancelar"
      class="mr-2 text-sm font-light"
      severity="secondary"
      @click="router.back()"
    />
    <Button
      label="Guardar"
      class="text-sm font-light"
      severity="primary"
      @click="onSave"
      :disabled="!isValidToSave"
    />
  </Footer>
  <Dialog
    v-model:visible="isEditorModalVisible"
    class="p-dialog-maximized"
    modal
  >
    <template #header>
      <HeadTitle
        title="Editor de carta"
        description="Editor de carta para personalizar el contenido de la carta"
      />
    </template>
    <div class="flex flex-column h-full">
      <Editor
        ref="letterEditorComponent"
        :template="template"
        :page-properties="{ margins: pageMargins }"
      />
    </div>
    <template #footer>
      <FooterEditor
        ref="footerEditRef"
        @on-preview="onPreview"
        @save-editor-content="onSaveEditorContent"
        :header-img="headerImg"
        :footer-img="footerImg"
      />
    </template>
  </Dialog>
</template>
<script lang="ts" setup>
  import Step from '@/shared/components/Step.vue'
  import Footer from '@/shared/components/Footer.vue'
  import AuditButton from '@/shared/components/AuditButton.vue'
  import MultiSelectDialog from '@/shared/components/MultiSelectDialog.vue'
  import { useField } from 'vee-validate'
  import * as yup from 'yup'
  import Approver from '@/modules/approvers/models/Approver'
  import { getApproversByCompanyId } from '@/modules/approvers/services/ApproverService'
  import useCompany from '@/store/company'
  import { storeToRefs } from 'pinia'
  import Payroll from '@/modules/payrolls-config/models/Payroll'
  import { getPayrollsByCompanyId } from '@/modules/payrolls-config/services/PayrollService'
  import {
    getLetterConfigById,
    getPreviewLetter,
    updateLetterConfig,
  } from '../services/LetterConfgServices'
  import router from '@/router'
  import useNotifications from '@/store/notification'
  import useLoading from '@/store/load'
  import { ref, computed, onMounted, reactive, nextTick, watch } from 'vue'
  import { useRoute } from 'vue-router'
  import IUpdateLetterConfig from '../services/dtos/IUpdateLetterConfig'
  import Editor from '../components/Editor.vue'
  import InfoButton from '@/shared/components/InfoButton.vue'
  import HeadTitle from '@/shared/components/HeadTitle.vue'
  import EditRecordButton from '@/shared/components/EditRecordButton.vue'
  import FooterEditor from '../components/FooterEditor.vue'
  import LetterApproversForm from '../components/LetterApproversForm.vue'
  import { ILetterApproverDetail } from '../services/dtos/ILetterApproverDetail'
  import { getFileUrlPresigned } from '@/shared/services/fileService'

  const path = useRoute()
  const letterConfigId = ref()
  const companyStore = useCompany()
  const loadingStore = useLoading()
  const notificationStore = useNotifications()
  const { company } = storeToRefs(companyStore)
  const letterEditorComponent = ref<any>(null)
  const isEditorModalVisible = ref(false)
  const footerEditRef = ref()
  const headerImg = ref()
  const footerImg = ref()

  companyStore.$subscribe(async (_, state) => {
    company.value = state.company
    await fetchApprovers()
    await fetchPayrolls()
  })

  const approvalMethodsOpts = ref([
    { label: 'Requiere aprobación', value: 'WITH_APPROVAL' },
    {
      label: 'Requiere Multiple aprobaciones',
      value: 'WITH_MULTIPLE_APPROVALS',
    },
    { label: 'Automática', value: 'AUTOMATIC' },
  ])

  let pageMargins = reactive({
    top: 0.39 * 2,
    bottom: 0.39 * 2,
    left: 0.39 * 2,
    right: 0.39 * 2,
  })

  const approvers = ref<Approver[]>()
  const payrolls = ref<Payroll[]>()
  const isEditMode = ref<boolean>(false)
  const isSubmitting = ref(false)
  const MAX_APPROVERS = 8

  const nameSchema = yup.string().required('El nombre es requerido')
  const descriptionSchema = yup.string().required('La descripción es requerida')
  const generationTypeSchema = yup
    .string()
    .required('El método de aprobación es requerido')
  const payrollsSchema = yup
    .array()
    .min(1, 'Al menos una nómina debe ser seleccionado')
    .required('Las nóminas son requeridas')
  const emailsSchema = yup
    .array()
    .test('emails', 'Uno de los correos es invalido', (value) => {
      if (value) {
        const emails = value
        const regex = new RegExp(
          '^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,4}$'
        )
        return emails.every((email) => regex.test(email.trim()))
      }
      return true
    })

  // for multiple approvers selection
  const approversSchema = yup.array(
    yup.object().shape({
      approverId: yup.string().required('Selecciona un aprobador'),
      approverName: yup
        .string()
        .required('El nombre del aprobador es requerido'),
      approverPosition: yup.string().optional().nullable(),
      approverPhone: yup.string().optional().nullable(),
      approverEmail: yup.string().optional().nullable(),
    })
  )

  // for single approver selection
  const approverSchema = yup.string().required('El aprobador es requerido')

  const {
    value: approver,
    errorMessage: approverError,
    validate: approverValidate,
  } = useField<string>('approver', approverSchema)

  const {
    value: description,
    errorMessage: descriptionError,
    validate: descValidate,
  } = useField<string>('description', descriptionSchema)
  const {
    value: name,
    errorMessage: nameError,
    validate: nameValidate,
  } = useField<string>('description', nameSchema)
  const {
    value: generationType,
    errorMessage: generationTypedError,
    validate: modValidate,
  } = useField<string>('generationType', generationTypeSchema)
  const {
    value: selectedPayrolls,
    errorMessage: payrollsError,
    validate: payrollsValidate,
  } = useField<string[]>('selectedPayrolls', payrollsSchema)
  const {
    value: emailsToNotify,
    errorMessage: emailsError,
    validate: emailsValidate,
  } = useField<string[]>('emailsToNotify', emailsSchema)
  const {
    value: approversData,
    errorMessage: approversError,
    validate: approversValidate,
  } = useField<ILetterApproverDetail[]>('approversData', approversSchema, {
    initialValue: [],
  })
  approversData.value = approversData.value ?? []

  const createEmptyApprover = (): ILetterApproverDetail => ({
    id: undefined,
    approverId: '',
    approverName: '',
    approverPosition: null,
    approverPhone: null,
    approverEmail: null,
    signature: undefined,
    signatureUrl: null,
    isDeleted: false,
  })

  const ensureApproverRows = () => {
    if (generationType.value !== 'WITH_MULTIPLE_APPROVALS') {
      approversData.value = []
      return
    }

    if (!approversData.value || approversData.value.length === 0) {
      approversData.value = [createEmptyApprover()]
    }

    if (approversData.value.length > MAX_APPROVERS) {
      approversData.value = approversData.value.slice(0, MAX_APPROVERS)
    }
  }

  watch(
    () => generationType.value,
    () => {
      if (generationType.value === 'WITH_MULTIPLE_APPROVALS') {
        ensureApproverRows()
      } else {
        approversData.value = []
      }
    },
    { immediate: true }
  )
  const template = ref<string>('')

  const resolveApproverSignaturePreviews = async (
    items: ILetterApproverDetail[]
  ) => {
    if (!items || items.length === 0) {
      return items ?? []
    }

    let hasSignatureErrors = false

    const withPreviews = await Promise.all(
      items.map(async (approver) => {
        if (!approver?.signature) {
          return approver
        }

        if (approver.signature instanceof File) {
          return approver
        }

        if (typeof approver.signature !== 'string') {
          return approver
        }

        const signatureKey = approver.signature

        if (!signatureKey) {
          return approver
        }

        if (signatureKey.startsWith('http')) {
          return {
            ...approver,
            signatureUrl: signatureKey,
          }
        }

        try {
          const presignedUrl = await getFileUrlPresigned(signatureKey)
          return {
            ...approver,
            signatureUrl: presignedUrl ?? approver.signatureUrl ?? null,
          }
        } catch (error) {
          console.error('Error fetching signature preview URL', error)
          hasSignatureErrors = true
          return approver
        }
      })
    )

    if (hasSignatureErrors) {
      notificationStore.addErrorNotification({
        detail:
          'No se pudieron cargar algunas firmas. Puedes intentar recargar la página o volver a subirlas.',
        severity: 'error',
        summary: 'Error al cargar firmas',
      })
    }

    return withPreviews
  }

  onMounted(async () => {
    loadingStore.setLoadingState()
    const { id } = path.params
    letterConfigId.value = String(id)
    if (letterConfigId.value) {
      const letterConfig = await getLetterConfigById(letterConfigId.value)
      name.value = letterConfig.name
      description.value = letterConfig.description || ''
      approver.value = letterConfig.approver?.id || ''
      generationType.value = letterConfig.generationType
      if (letterConfig.generationType === 'WITH_MULTIPLE_APPROVALS') {
        const approversFromApi = (letterConfig.letterApprovers || []).map(
          (approverItem) => ({
            id: approverItem.id,
            approverId: approverItem.approverId || '',
            approverName: approverItem.approverName || '',
            signature: approverItem.signature ?? null,
            signatureUrl: null,
            approverPosition: approverItem.approverPosition ?? null,
            approverPhone: approverItem.approverPhone ?? null,
            approverEmail: approverItem.approverEmail ?? null,
            isDeleted: false,
          })
        )

        approversData.value = await resolveApproverSignaturePreviews(
          approversFromApi
        )
      } else {
        approversData.value = []
      }
      selectedPayrolls.value = letterConfig.payrolls.map(
        (payroll: any) => payroll.id
      )
      emailsToNotify.value = letterConfig.emailsToNotify || []
      template.value = letterConfig.template || ''
      pageMargins = letterConfig.pageProperties?.margins || pageMargins
      headerImg.value = letterConfig.header || null
      footerImg.value = letterConfig.footer || null
      companyStore.setCompany({
        id: letterConfig.company.id,
        companyId: letterConfig.company.companyId,
        code: letterConfig.company.code,
        name: letterConfig.company.name,
        client: {
          id: letterConfig.company.client.id,
          code: letterConfig.company.client.code,
          name: letterConfig.company.client.name,
        },
      })
    }

    await fetchApprovers()
    await fetchPayrolls()
    ensureApproverRows()
    loadingStore.dismissLoadingState()
  })

  const fetchApprovers = async () => {
    if (company.value?.id) {
      const reponse = await getApproversByCompanyId(company.value?.id)
      approvers.value = reponse
    } else {
      notificationStore.addErrorNotification({
        detail: `No se ha seleccionado una compañia`,
        severity: 'error',
        summary: `Error al cargar aprobadores`,
      })
    }
  }

  const fetchPayrolls = async () => {
    if (company.value?.id) {
      const response = await getPayrollsByCompanyId(company.value?.id || '')
      payrolls.value = payrolls.value = response.map((payroll) => ({
        ...payroll,
        name: payroll.code + ' - ' + payroll.name,
      }))
    } else {
      notificationStore.addErrorNotification({
        detail: `No se ha seleccionado una compañia`,
        severity: 'error',
        summary: `Error al cargar nóminas`,
      })
    }
  }

  const onSave = async () => {
    await nameValidate()
    await descValidate()
    await modValidate()
    if (generationType.value === 'WITH_MULTIPLE_APPROVALS') {
      await approversValidate()
    } else {
      approversData.value = []
    }
    await payrollsValidate()
    await emailsValidate()

    if (!isValidToSave.value || !company.value) {
      return
    }

    loadingStore.setLoadingState()
    isSubmitting.value = true

    try {
      const primaryApproverId =
        generationType.value === 'WITH_APPROVAL' ? approver.value : undefined

      const letterConfig: IUpdateLetterConfig = {
        id: String(path.params.id) || '',
        name: name.value,
        description: description.value,
        generationType: generationType.value,
        approverId: primaryApproverId,
        companyId: company.value?.id || '',
        payrollsIds: selectedPayrolls.value,
        emailsToNotify: emailsToNotify.value,
        template: template.value,
        pageProperties: {
          margins: pageMargins,
        },
        footer: footerImg.value,
        header: headerImg.value,
        letterApprovers:
          generationType.value === 'WITH_MULTIPLE_APPROVALS'
            ? approversData.value?.map(
                ({ signature, signatureUrl, ...rest }) => ({
                  ...rest,
                  signature,
                })
              )
            : [],
      }

      const response = await updateLetterConfig(letterConfig)

      if (response === 'OK') {
        notificationStore.addSuccesNotification({
          detail: `200`,
          severity: 'success',
          summary: `Configuración de carta actualizada con exito`,
        })
      }
      router.push('/letterConfigs')
    } finally {
      isSubmitting.value = false
      loadingStore.dismissLoadingState()
    }
  }

  const setEditorContent = () => {
    const { editorRef, margins } = letterEditorComponent.value
    const content = editorRef
    template.value = content
    pageMargins = {
      top: Number(margins.top) / 2.54,
      bottom: Number(margins.bottom) / 2.54,
      left: Number(margins.left) / 2.54,
      right: Number(margins.right) / 2.54,
    }
    headerImg.value = footerEditRef.value.headerImg
    footerImg.value = footerEditRef.value.footerImg
  }

  const onSaveEditorContent = () => {
    setEditorContent()
    isEditorModalVisible.value = false
  }

  const onPreview = async () => {
    loadingStore.setLoadingState()
    setEditorContent()
    await nextTick()
    const buffer = await getPreviewLetter({
      template: template.value,
      header: headerImg.value,
      footer: footerImg.value,
      pageProperties: {
        margins: pageMargins,
      },
    })
    loadingStore.dismissLoadingState()

    const blob = new Blob([buffer], { type: 'application/pdf' })
    const url = URL.createObjectURL(blob)
    window.open(url, '_blank')
  }

  const isValidToSave = computed(() => {
    if (!isEditMode.value) {
      return false
    }

    const baseValid =
      !nameError.value &&
      !descriptionError.value &&
      !generationTypedError.value &&
      !payrollsError.value &&
      !emailsError.value

    if (generationType.value === 'WITH_MULTIPLE_APPROVALS') {
      return (
        baseValid &&
        (approversData.value?.length ?? 0) > 0 &&
        !approversError.value
      )
    }

    if (generationType.value === 'WITH_APPROVAL') {
      return baseValid && !approverError.value
    }

    return baseValid
  })

  const toggleEditMode = (value: boolean) => {
    isEditMode.value = value
    if (value && generationType.value === 'WITH_APPROVAL') {
      ensureApproverRows()
    }
  }
</script>
<style scoped>
  :deep(.p-button-label) {
    font-size: 0.8rem;
  }
</style>
